/**
 * useVoiceInput — Real-time voice→text (shared across go-pwa, web-client, extension-shell)
 *
 * Modes:
 *   ptt (Push-to-Talk) — caller must explicitly call stopRecording()
 *   vad (Voice Activity Detection) — auto-stops on silence (Web Speech API onend)
 *
 * Strategies:
 *   Primary:  Web Speech API (real-time interim transcript, no upload)
 *   Fallback: MediaRecorder + transcribeFn (backend STT) when SpeechRecognition unavailable
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import type {
  VoiceError,
  VoiceErrorCode,
  VoiceRole,
  VoiceSessionState,
  VoiceTelemetryEvent,
} from './voice-types';
import { resolveVoiceProfile } from './voice-profiles';
import {
  blobToBase64,
  buildVoiceError,
  getSpeechRecognitionCtor,
  getVoiceRuntimeEngine,
  mapSpeechRecognitionError,
  type SpeechRecognitionErrorEvent,
  type SpeechRecognitionEvent,
  type SpeechRecognitionInstance,
} from './voice-runtime';

/** Function to transcribe raw audio via backend STT (required for MediaRecorder fallback) */
export interface VoiceTranscribeContext {
  mode: 'ptt' | 'vad';
  role?: VoiceRole;
  surface?: string;
  profile: string;
  vocabularyHints: string[];
  engine: 'web_speech' | 'media_recorder';
  isRealtime: boolean;
}

export type TranscribeFn = (
  audioBase64: string,
  mimeType: string,
  lang?: string,
  context?: VoiceTranscribeContext,
) => Promise<string>;

export interface UseVoiceInputOpts {
  /** Called when a final transcription is available */
  onTranscription?: (text: string) => void;
  /** Called on errors */
  onError?: (msg: string) => void;
  /** BCP-47 language hint, e.g. 'en', 'zh-CN', 'ms' */
  lang?: string;
  /**
   * ptt  — Push-to-Talk: caller calls stopRecording() to finalize
   * vad  — Voice Activity Detection: auto-stops when speech ends
   * Default: 'ptt'
   */
  mode?: 'ptt' | 'vad';
  /**
   * Backend transcription function for MediaRecorder fallback.
   * Required when Web Speech API is unavailable (some browsers).
   */
  transcribeFn?: TranscribeFn;
  /** Optional voice profile id for telemetry and future runtime biasing */
  profile?: string;
  /** Optional role for telemetry and future runtime biasing */
  role?: VoiceRole;
  /** Optional surface id for telemetry and future runtime biasing */
  surface?: string;
  /** Optional vocabulary hints for future backend STT biasing */
  vocabularyHints?: string[];
  /** Called when session state changes */
  onSessionStateChange?: (state: VoiceSessionState) => void;
  /** Called with structured voice error details */
  onErrorDetail?: (error: VoiceError) => void;
  /** Called for telemetry-worthy events */
  onTelemetry?: (event: VoiceTelemetryEvent) => void;
}

export function useVoiceInput(opts: UseVoiceInputOpts = {}) {
  const { mode = 'ptt' } = opts;
  const resolvedProfile = resolveVoiceProfile(opts.profile, {
    role: opts.role,
    lang: opts.lang,
    vocabularyHints: opts.vocabularyHints,
  });

  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [sessionState, setSessionState] = useState<VoiceSessionState>('idle');
  const [lastError, setLastError] = useState<VoiceError | null>(null);
  /** Live interim text while speaking (Web Speech API only) */
  const [interimTranscript, setInterimTranscript] = useState('');
  /** Elapsed recording seconds */
  const [elapsed, setElapsed] = useState(0);

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const finalTextRef = useRef('');
  const sessionStartedAtRef = useRef<number | null>(null);
  const sessionStateRef = useRef<VoiceSessionState>('idle');

  const webSpeechSupported = typeof window !== 'undefined' && !!getSpeechRecognitionCtor();
  const runtimeEngine = getVoiceRuntimeEngine(webSpeechSupported);

  const updateSessionState = useCallback((next: VoiceSessionState) => {
    sessionStateRef.current = next;
    setSessionState(next);
    opts.onSessionStateChange?.(next);
  }, [opts.onSessionStateChange]);

  const emitTelemetry = useCallback((event: Omit<VoiceTelemetryEvent, 'mode' | 'sessionState' | 'engine'> & { name: VoiceTelemetryEvent['name']; sessionState?: VoiceSessionState }) => {
    opts.onTelemetry?.({
      ...event,
      mode,
      engine: runtimeEngine,
      sessionState: event.sessionState ?? sessionStateRef.current,
      role: opts.role,
      surface: opts.surface,
      profile: resolvedProfile.id,
      lang: resolvedProfile.lang,
      isRealtime: webSpeechSupported,
    });
  }, [mode, opts.onTelemetry, opts.role, opts.surface, resolvedProfile.id, resolvedProfile.lang, runtimeEngine, webSpeechSupported]);

  const reportError = useCallback((code: VoiceErrorCode, message: string, cause?: unknown) => {
    const error: VoiceError = buildVoiceError(code, message, cause);
    setLastError(error);
    updateSessionState('failed');
    opts.onErrorDetail?.(error);
    opts.onError?.(message);
    emitTelemetry({
      name: 'voice.transcription_fail',
      sessionState: 'failed',
      errorCode: code,
    });
  }, [emitTelemetry, opts.onError, opts.onErrorDetail, updateSessionState]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      recognitionRef.current?.abort();
      if (mediaRecorderRef.current?.state !== 'inactive') {
        mediaRecorderRef.current?.stop();
      }
    };
  }, []);

  const startTimer = useCallback(() => {
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((s: number) => s + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  // ── Web Speech API ────────────────────────────────────────────────────────

  const startWebSpeech = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;

    const recognition = new Ctor();
    // PTT = keep listening until explicit stop; VAD = auto-stop at end of speech
    recognition.continuous = mode === 'ptt';
    recognition.interimResults = true;
    recognition.lang = resolvedProfile.lang;
    finalTextRef.current = '';

    recognition.onresult = (ev: SpeechRecognitionEvent) => {
      let allFinal = '';
      let interim = '';
      for (let i = 0; i < ev.results.length; i++) {
        const result = ev.results[i]!;
        const text = result[0]!.transcript;
        if (result.isFinal) {
          allFinal += text;
        } else {
          interim += text;
        }
      }
      finalTextRef.current = allFinal;
      setTranscript(allFinal);
      setInterimTranscript(interim);
    };

    recognition.onerror = (ev: SpeechRecognitionErrorEvent) => {
      const mappedError = mapSpeechRecognitionError(ev.error);
      if (!mappedError) return;
      reportError(mappedError.code, mappedError.message, ev);
    };

    recognition.onend = () => {
      stopTimer();
      setRecording(false);
      setInterimTranscript('');
      const text = finalTextRef.current.trim();
      if (text) {
        updateSessionState('completed');
        emitTelemetry({
          name: 'voice.transcription_success',
          sessionState: 'completed',
          transcriptLength: text.length,
          latencyMs: sessionStartedAtRef.current ? Math.round(performance.now() - sessionStartedAtRef.current) : undefined,
        });
        opts.onTranscription?.(text);
      } else if (sessionStateRef.current !== 'failed') {
        updateSessionState('idle');
      }
      emitTelemetry({ name: 'voice.session_end', sessionState: text ? 'completed' : sessionStateRef.current });
      finalTextRef.current = '';
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setLastError(null);
    sessionStartedAtRef.current = performance.now();
    recognition.start();
    setRecording(true);
    setTranscript('');
    setInterimTranscript('');
    updateSessionState('listening');
    emitTelemetry({ name: 'voice.session_start', sessionState: 'listening' });
    startTimer();
  }, [emitTelemetry, mode, opts.onTranscription, reportError, resolvedProfile.lang, startTimer, stopTimer, updateSessionState]);

  const stopWebSpeech = useCallback(() => {
    recognitionRef.current?.stop();
    stopTimer();
    setRecording(false);
  }, [stopTimer]);

  // ── MediaRecorder fallback ────────────────────────────────────────────────

  const startMediaRecorder = useCallback(async () => {
    if (!opts.transcribeFn) {
      reportError('unsupported_browser', 'Voice input (backend transcription) not configured for this environment.');
      return;
    }
    try {
      updateSessionState('requesting_permission');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        stopTimer();

        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        emitTelemetry({ name: 'voice.session_end', sessionState: 'transcribing' });
        if (blob.size < 100) { reportError('recording_too_short', 'Recording too short'); return; }

        setTranscribing(true);
        updateSessionState('transcribing');
        try {
          const base64 = await blobToBase64(blob);
          const text = await opts.transcribeFn!(
            base64,
            recorder.mimeType,
            resolvedProfile.lang,
            {
              mode,
              role: opts.role,
              surface: opts.surface,
              profile: resolvedProfile.id,
              vocabularyHints: resolvedProfile.vocabularyHints,
              engine: runtimeEngine,
              isRealtime: webSpeechSupported,
            },
          );
          setTranscript(text);
          if (text) {
            updateSessionState('completed');
            emitTelemetry({
              name: 'voice.transcription_success',
              sessionState: 'completed',
              transcriptLength: text.length,
              latencyMs: sessionStartedAtRef.current ? Math.round(performance.now() - sessionStartedAtRef.current) : undefined,
            });
            opts.onTranscription?.(text);
          } else {
            reportError('no_speech', 'No speech detected');
          }
        } catch (err) {
          reportError('network_error', err instanceof Error ? err.message : 'Transcription failed', err);
        } finally {
          setTranscribing(false);
        }
      };

      recorder.start(250);
      mediaRecorderRef.current = recorder;
      setLastError(null);
      sessionStartedAtRef.current = performance.now();
      setRecording(true);
      setTranscript('');
      setInterimTranscript('');
      updateSessionState('listening');
      emitTelemetry({ name: 'voice.fallback_used', sessionState: 'listening' });
      emitTelemetry({ name: 'voice.session_start', sessionState: 'listening' });
      startTimer();
    } catch (err) {
      const msg = err instanceof DOMException && err.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow microphone in browser settings.'
        : 'Failed to access microphone';
      reportError(err instanceof DOMException && err.name === 'NotAllowedError' ? 'permission_denied' : 'network_error', msg, err);
    }
  }, [emitTelemetry, opts.onTranscription, opts.transcribeFn, reportError, resolvedProfile.lang, startTimer, stopTimer, updateSessionState]);

  const stopMediaRecorder = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
    stopTimer();
  }, [stopTimer]);

  // ── Unified API ───────────────────────────────────────────────────────────

  const startRecording = useCallback(async () => {
    if (webSpeechSupported) {
      startWebSpeech();
    } else {
      await startMediaRecorder();
    }
  }, [webSpeechSupported, startWebSpeech, startMediaRecorder]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      stopWebSpeech();
    } else {
      stopMediaRecorder();
    }
  }, [stopWebSpeech, stopMediaRecorder]);

  const toggleRecording = useCallback(() => {
    if (recording) stopRecording();
    else void startRecording();
  }, [recording, startRecording, stopRecording]);

  return {
    recording,
    transcribing,
    transcript,
    interimTranscript,
    elapsed,
    sessionState,
    lastError,
    /** Whether using real-time Web Speech API (vs MediaRecorder upload fallback) */
    isRealtime: webSpeechSupported,
    startRecording,
    stopRecording,
    toggleRecording,
  };
}
